import { useEffect, useRef, useState } from "react";
import { Col, Row } from "react-bootstrap";
import { useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { v4 as uuidv4 } from "uuid";
import {
  useCreateSolarEstimationMutation,
  useInitiateSolarEstimationMutation,
  useLazyViewSolarEstimationQuery,
  usePersistEstimationMutation,
  useUpdateEstimationMutation,
} from "../../../redux/api/solar-estimation/solarEstimationAPI";
import { selectAuth } from "../../../redux/features/auth/auth-slice";
import { selectCountry } from "../../../redux/features/user/user-slice";
import { PersistEstimationResponseDTO } from "../../../shared/oversight-core/dtos/response-dtos/persist-estimation-response-dto";
import {
  IViewSolarEstimationData,
  IViewSolarEstimationOutputParams,
  IViewSolarUserSelectedProducts,
  ViewSolarEstimationResponseDTO,
} from "../../../shared/oversight-core/dtos/response-dtos/view-solar-estimation-response-dto";
import { EApplicationType } from "../../../shared/oversight-core/enums/application-type";
import { EDeviceTypes } from "../../../shared/oversight-core/enums/device-types";
import { EEnergyNeedEditParameter } from "../../../shared/oversight-core/enums/energy-need-edit-parameters";
import { EEstimationType } from "../../../shared/oversight-core/enums/estimation-type";
import { EInverterPhaseType } from "../../../shared/oversight-core/enums/inverter-phase-type";
import { ESolarSolutionType } from "../../../shared/oversight-core/enums/sola-solution-type";
import { ESolarSimBrands } from "../../../shared/oversight-core/enums/solar-sim-brands";
import { ESolarSimLocalStorage } from "../../../shared/oversight-core/enums/solar-sim-local-storage";
import { ESolarSystemType } from "../../../shared/oversight-core/enums/solar-system-type ";
import { ETimePeriod } from "../../../shared/oversight-core/enums/time-period";
import { EUtilityOptionType } from "../../../shared/oversight-core/enums/utility-option-type";
import { IAdvancedSave } from "../../../shared/oversight-core/interfaces/advanced-save";
import { AppRoute } from "../../../shared/oversight-core/interfaces/app-routes";
import { Configurations } from "../../../shared/oversight-core/interfaces/configurations";
import { IEstimatorDetails } from "../../../shared/oversight-core/interfaces/estimator-details";
import { ISimplifiedSave } from "../../../shared/oversight-core/interfaces/simplified-save";
import { Option } from "../../../shared/oversight-core/ui-elements/app-select/app-select";
import MaterialIcon from "../../../shared/oversight-core/ui-elements/material-icon/material-icon";
import SpinnerModal from "../../../shared/oversight-core/ui-elements/spinner/spinner";
import { ISteps } from "../../../shared/oversight-core/ui-elements/steps-number/steps-number";
import {
  showErrorMessage,
  showSuccessMessage,
} from "../../../shared/oversight-core/utils/toast";
import AddUsageInfo from "../components/add-usage-info/add-usage-info";
import ApprovedSystem from "../components/approved-system/approved-system";
import AssembleSystem from "../components/assemble-system/assemble-system";
import EditParameterModal from "../components/edit-parameter-modal/edit-parameter-modal";
import IncludeSystemCard from "../components/include-system-card/include-system-card";
import RemoveSolarSystemModal from "../components/remove-solar-system-modal/remove-solar-system-modal";
import RemoveWaterHeaterSystemModal from "../components/remove-water-heater-system-modal/remove-water-heater-system-modal";
import SaveDataModal from "../components/save-data-modal/save-data-modal";
import SelectedSolarSystemCard from "../components/selected-solar-system-card/selected-solar-system-card";
import SelectedWaterHeaterSystemCard from "../components/selected-water-heater-system-card/selected-water-heater-system-card";
import { ISimplifiedDevice } from "../components/simplified-device-energy-card/simplified-device-energy-card";
import StepsHeader from "../components/steps-header/steps-header";
import WaterHeaterSystemModal from "../components/water-heater-system-modal/water-heater-system-modal";
import styles from "./build-system-simplified.module.scss";

const steps: ISteps[] = [
  {
    id: 1,
    stepName: "Add Usage Info",
    stepIcon: "devices_other",
  },
  { id: 2, stepName: "Assemble System", stepIcon: "widgets" },
];

const solarSystemTypesOption: Option[] = [
  {
    value: ESolarSystemType.BACKUP,
    label: "Backup",
  },
  {
    value: ESolarSystemType.OFF_GRID,
    label: "Off-Grid",
  },
  {
    value: ESolarSystemType.HYBRID,
    label: "Hybrid",
  },
];

const inverterPhaseTypeOptions: Option[] = [
  {
    value: EInverterPhaseType.SINGLE_PHASE,
    label: "1 Phase",
  },
  {
    value: EInverterPhaseType.THREE_PHASE,
    label: "3 Phase",
  },
  {
    value: EInverterPhaseType.NONE,
    label: "None",
  },
];

const applicationTypeOptions: Option[] = [
  {
    value: EApplicationType.RESIDENTIAL,
    label: "Residential",
  },
  {
    value: EApplicationType.HOTELS,
    label: "Hotels",
  },
  {
    value: EApplicationType.HOSTELS,
    label: "Hostels",
  },
  {
    value: EApplicationType.SCHOOLS,
    label: "Schools",
  },
];

const utilityOptions: Option[] = [
  {
    value: EUtilityOptionType.SHOWER,
    label: "Shower",
  },
  {
    value: EUtilityOptionType.BATHTUB_JACUZZI,
    label: "Bathtub/Jacuzzi",
  },
];

const systemVoltageOptions: Option[] = [
  {
    value: "12",
    label: "12V",
  },
  {
    value: "24",
    label: "24V",
  },
  {
    value: "48",
    label: "48V",
  },
];

interface ILocationState {
  systemType: ESolarSystemType;
  numberOfUsers: number;
  demandPerUser: number;
  applicationType: EApplicationType;
  utilityOption: EUtilityOptionType;
  solarSolutionType: ESolarSolutionType;
  inverterPhaseType: EInverterPhaseType;
  estimationId: string;
  estimationToken: string;
}

const BuildSystemSimplified = () => {
  const auth = useSelector(selectAuth);
  const country = useSelector(selectCountry);
  const navigate = useNavigate();
  const location = useLocation();
  const pathname = location.pathname;
  const locationState = location.state as ILocationState;

  const [estimatorDetails, setEstimatorDetails] = useState<IEstimatorDetails>({
    estimationId: "",
    estimationToken: "",
  });
  const [showEditParameterModal, setShowEditParameterModal] = useState(false);
  const [editParameterValue, setEditParameterValue] = useState<number | string>(
    0,
  );
  const [editParameterType, setEditParameterType] =
    useState<EEnergyNeedEditParameter>(EEnergyNeedEditParameter.TITLE);
  const [title, setTitle] = useState("");
  const [monthlyEnergyNeed, setMonthlyEnergyNeed] = useState(0);
  const [monthlyEnergyNeedFromUser, setMonthlyEnergyNeedFromUser] = useState(0);
  const [maxPower, setMaxPower] = useState(0);
  const [maxPowerFromUser, setMaxPowerFromUser] = useState(0);
  const [dischargeRate, setDischargeRate] = useState(0);
  const [nightLoad, setNightLoad] = useState(60);
  const [sunlightHours, setSunlightHours] = useState(5);
  const [autonomyDays, setAutonomyDays] = useState<number | null>(null);
  const [systemVoltage, setSystemVoltage] = useState<Option | null>(null);
  const [estimatedValue, setEstimatedValue] = useState<number | undefined>(
    undefined,
  );
  const [devices, setDevices] = useState<ISimplifiedDevice[]>([
    {
      deviceType: EDeviceTypes.TV,
      deviceName: "Televisions",
      amount: "",
      wattage: "",
      usage: "",
      id: uuidv4(),
      units: 0,
      maxPower: 0,
    },
    {
      deviceType: EDeviceTypes.BULB,
      deviceName: "Lights",
      amount: "",
      wattage: "",
      usage: "",
      id: uuidv4(),
      units: 0,
      maxPower: 0,
    },
    {
      deviceType: EDeviceTypes.COMPUTER,
      deviceName: "Computers",
      amount: "",
      wattage: "",
      usage: "",
      id: uuidv4(),
      units: 0,
      maxPower: 0,
    },
    {
      deviceType: EDeviceTypes.AIR_CONDITIONER,
      deviceName: "Air Conditioners",
      amount: "",
      wattage: "",
      usage: "",
      id: uuidv4(),
      units: 0,
      maxPower: 0,
    },
    {
      deviceType: EDeviceTypes.RICE_COOKER,
      deviceName: "Rice Cookers",
      amount: "",
      wattage: "",
      usage: "",
      id: uuidv4(),
      units: 0,
      maxPower: 0,
    },
    {
      deviceType: EDeviceTypes.REFRIGERATOR,
      deviceName: "Refrigerators",
      amount: "",
      wattage: "",
      usage: "",
      id: uuidv4(),
      units: 0,
      maxPower: 0,
    },
    {
      deviceType: EDeviceTypes.WASHING_MACHINE,
      deviceName: "Washing Machines",
      amount: "",
      wattage: "",
      usage: "",
      id: uuidv4(),
      units: 0,
      maxPower: 0,
    },
    {
      deviceType: EDeviceTypes.GENERAL,
      deviceName: "Others",
      amount: "",
      wattage: "",
      usage: "",
      id: uuidv4(),
      units: 0,
      maxPower: 0,
    },
  ]);
  const [selectedStep, setSelectedStep] = useState(1);
  const [selectedSystemOutput, setSelectedSystemOutput] =
    useState<IViewSolarEstimationOutputParams>({
      totalWattHours: 0,
      totalCost: 0,
      systemVoltage: 0,
      totalAmpHours: 0,
      nightLoadAmpHours: 0,
      batteryAmpHours: 0,
      maxPowerInWatt: 0,
      grandTotal: 0,
      totalAccessoriesCost: 0,
      laborCost: 0,
      totalLiters: 0,
    });
  const [isResetRecommendations, setIsResetRecommendations] = useState(false);
  const [assembleSystemData, setAssembleSystemData] = useState<
    IViewSolarEstimationData | undefined
  >(undefined);
  const [scheduleId, setScheduleId] = useState<string>("");
  const [userSelectedProducts, setUserSelectedProducts] = useState<
    IViewSolarUserSelectedProducts[]
  >([]);
  const [currency, setCurrency] = useState("");
  const [isShowSaveDataModal, setIsShowSaveDataModal] = useState(false);
  const [configurations, setConfigurations] = useState<Configurations>();
  const [includeOtherFees, setIncludeOtherFees] = useState(false);
  const [estimationId, setEstimationId] = useState(0);
  const [comment, setComment] = useState("");
  const [isReset, setIsReset] = useState(false);
  const [hasChanged, setHasChanged] = useState(false);
  const [solarSystemType, setSolarSystemType] = useState<Option>({
    ...solarSystemTypesOption[2],
  });
  const [inverterPhaseType, setInverterPhaseType] = useState<Option>({
    ...inverterPhaseTypeOptions[0],
  });
  const [applicationType, setApplicationType] = useState<Option>({
    ...applicationTypeOptions[0],
  });
  const [solarSolutionType, setSolarSolutionType] =
    useState<ESolarSolutionType>(ESolarSolutionType.BOTH);

  const [utilityOption, setUtilityOption] = useState<Option>({
    ...utilityOptions[0],
  });
  const [numberOfUsers, setNumberOfUsers] = useState(0);
  const [demandPerUser, setDemandPerUser] = useState<number | null>(null);
  const [waterDemand, setWaterDemand] = useState(0);
  const [isRemoveWaterHeaterSystem, setIsRemoveWaterHeaterSystem] =
    useState(false);
  const [isShowRemoveSolarSystemModal, setIsShowRemoveSolarSystemModal] =
    useState(false);
  const [
    isShowRemoveWaterHeaterSystemModal,
    setIsShowRemoveWaterHeaterSystemModal,
  ] = useState(false);
  const [isUpdateCurrentState, setIsUpdateCurrentState] = useState(false);
  const [isShowWaterHeaterSystemModal, setIsShowWaterHeaterSystemModal] =
    useState(false);
  const [isAddWaterHeaterSystem, setIsAddWaterHeaterSystem] = useState(false);
  const [controllerRequired, setControllerRequired] = useState(false);

  const hasHydrated = useRef(false);

  const [
    triggerInitiateSolarEstimation,
    { isLoading: isLoadingInitiateSolarEstimation },
  ] = useInitiateSolarEstimationMutation();
  const [triggerPersistEstimation, { isLoading: isLoadingPersistEstimation }] =
    usePersistEstimationMutation();
  const [triggerUpdateEstimation, { isLoading: isLoadingUpdateEstimation }] =
    useUpdateEstimationMutation();
  const [
    triggerLazyViewSolarEstimation,
    { isLoading: isLoadingViewEstimation },
  ] = useLazyViewSolarEstimationQuery();
  const [
    triggerCreateSolarEstimation,
    { isLoading: isLoadingCreateSolarEstimation },
  ] = useCreateSolarEstimationMutation();

  useEffect(() => {
    if (solarSystemType.value) {
      setHasChanged(true);
    }
  }, [solarSystemType.value]);

  useEffect(() => {
    const unparsedEstimatorDetails = localStorage.getItem(
      ESolarSimLocalStorage.ESTIMATOR_DETAILS_SIMPLIFIED,
    );

    const globalConfigurations = localStorage.getItem("globalConfigurations");

    if (unparsedEstimatorDetails) {
      const parsedEstimatorDetails: IEstimatorDetails = JSON.parse(
        unparsedEstimatorDetails,
      );

      triggerLazyViewSolarEstimation(
        auth.token
          ? {
              estimationId: parsedEstimatorDetails.estimationId,
            }
          : {
              estimatorToken: parsedEstimatorDetails.estimationToken,
            },
      )
        .unwrap()
        .then((res) => {
          const inputParams = res.estimationView.data.inputParams;

          const selectedSolarTypeOption = solarSystemTypesOption.find(
            (s) => s.value === res.estimationView.data.inputParams.systemType,
          );

          const selectedSystemVoltageOption = systemVoltageOptions.find(
            (s) =>
              Number(s.value) ===
              res.estimationView.data.inputParams.systemVoltage,
          );

          const selectedInverterPhaseTypeOption = inverterPhaseTypeOptions.find(
            (i) => i.value === res.estimationView.inverterPhaseType,
          );

          const selectedUtilityOption = utilityOptions.find(
            (u) => u.value === res.estimationView.utilityOptionType,
          );

          const selectedApplicationType = applicationTypeOptions.find(
            (a) => a.value === res.estimationView.applicationType,
          );

          setWaterDemand(res.estimationView.data.inputParams.waterDemand || 0);
          setNumberOfUsers(
            res.estimationView.data.inputParams.numberOfUsers || 0,
          );
          setAutonomyDays(
            res.estimationView.data.inputParams.autonomyDays || 0,
          );
          setDemandPerUser(
            res.estimationView.data.inputParams.demandPerUser || 0,
          );
          setSolarSolutionType(res.estimationView.estimationType);
          setEstimationId(res.estimationView.estimationId);
          setTitle((ps) => res.estimationView.basicSchedule?.title || ps);
          setIncludeOtherFees(res.estimationView.installationCostRequested);
          setControllerRequired(res.estimationView.controllerRequired);
          setDischargeRate(inputParams.dischargeRatePercentage || 0);
          setNightLoad(inputParams.nightLoadPercentage || 60);
          setSunlightHours(inputParams.sunlightHours || 5);
          setDevices((ps) => {
            const devices = ps.map((d) => {
              const device =
                res.estimationView.basicSchedule?.powerConsumerUsages.find(
                  (p) => p.name === d.deviceName,
                );
              if (device) {
                return {
                  ...d,
                  amount: String(device.amount),
                  wattage: String(device.wattage),
                  usage: String(device.usageInHours),
                  units:
                    device.amount *
                    (device.wattage / 1000) *
                    device.usageInHours,
                  maxPower: device.amount * device.wattage,
                };
              }
              return d;
            });

            const deviceNames = devices.map((d) => d.deviceName);

            const userAddedDevices =
              res.estimationView.basicSchedule?.powerConsumerUsages.filter(
                (pcu) => !deviceNames.includes(pcu.name),
              ) || [];

            userAddedDevices.forEach((d) => {
              devices.push({
                deviceType: d.type,
                deviceName: d.name,
                amount: String(d.amount),
                wattage: String(d.wattage),
                usage: String(d.usageInHours),
                id: uuidv4(),
                units: 0,
                maxPower: 0,
              });
            });

            return [...devices];
          });
          setMonthlyEnergyNeed(inputParams.monthlyRequiredEnergy || 0);
          setMaxPower(inputParams.maxRequiredPower || 0);
          setSelectedSystemOutput(
            res.estimationView.data.outputParams || {
              totalWattHours: 0,
              totalCost: 0,
              systemVoltage: 0,
              totalAmpHours: 0,
              nightLoadAmpHours: 0,
              batteryAmpHours: 0,
              maxPowerInWatt: 0,
              grandTotal: 0,
              totalAccessoriesCost: 0,
              laborCost: 0,
              totalLiters: 0,
            },
          );

          if (selectedSolarTypeOption) {
            setSolarSystemType({
              value: selectedSolarTypeOption.value,
              label: selectedSolarTypeOption.label,
            });
          }

          if (selectedSystemVoltageOption) {
            setSystemVoltage({
              value: selectedSystemVoltageOption.value,
              label: selectedSystemVoltageOption.label,
            });
          }

          if (selectedInverterPhaseTypeOption) {
            setInverterPhaseType({
              value: selectedInverterPhaseTypeOption.value,
              label: selectedInverterPhaseTypeOption.label,
            });
          }

          if (selectedApplicationType) {
            setApplicationType({
              value: selectedApplicationType.value,
              label: selectedApplicationType.label,
            });
          }

          if (selectedUtilityOption) {
            setUtilityOption({
              value: selectedUtilityOption.value,
              label: selectedUtilityOption.label,
            });
          }

          if (res.estimationView.data.preferences) {
            if (globalConfigurations) {
              const parsedGlobalConfigurations: Configurations =
                JSON.parse(globalConfigurations);

              setConfigurations(parsedGlobalConfigurations);

              return;
            }

            setConfigurations(
              res.estimationView.data.preferences as Configurations,
            );
          }
        });

      setEstimatorDetails(parsedEstimatorDetails);
      return;
    }

    if (locationState && locationState?.estimationId) {
      triggerLazyViewSolarEstimation(
        auth.token
          ? {
              estimationId: locationState.estimationId,
            }
          : {
              estimatorToken: locationState?.estimationToken,
            },
      )
        .unwrap()
        .then((res: ViewSolarEstimationResponseDTO) => {
          setEstimationId(res.estimationView.estimationId);
          setTitle(res.estimationView.title || "");
          setEstimatorDetails({
            estimationId: res.estimationView.id,
            estimationToken: auth.token || locationState?.estimationToken,
          });
          setNumberOfUsers(
            res.estimationView.data.inputParams.numberOfUsers || 0,
          );
          setDemandPerUser(
            res.estimationView.data.inputParams.demandPerUser || 0,
          );
          setAutonomyDays(
            res.estimationView.data.inputParams.autonomyDays || 0,
          );
          setWaterDemand(res.estimationView.data.inputParams.waterDemand || 0);
          setSelectedSystemOutput(
            res.estimationView.data.outputParams || {
              totalWattHours: 0,
              totalCost: 0,
              systemVoltage: 0,
              totalAmpHours: 0,
              nightLoadAmpHours: 0,
              batteryAmpHours: 0,
              maxPowerInWatt: 0,
              grandTotal: 0,
              totalAccessoriesCost: 0,
              laborCost: 0,
              totalLiters: 0,
            },
          );
          setIncludeOtherFees(res.estimationView.installationCostRequested);
          setControllerRequired(res.estimationView.controllerRequired);

          const selectedSolarTypeOption = solarSystemTypesOption.find(
            (s) => s.value === locationState?.systemType,
          );

          const selectedInverterPhaseTypeOption = inverterPhaseTypeOptions.find(
            (i) => i.value === locationState.inverterPhaseType,
          );

          const selectedUtilityOption = utilityOptions.find(
            (u) => u.value === res.estimationView.utilityOptionType,
          );

          const selectedApplicationType = applicationTypeOptions.find(
            (a) => a.value === res.estimationView.applicationType,
          );

          const selectedSystemVoltageOption = systemVoltageOptions.find(
            (s) =>
              Number(s.value) ===
              res.estimationView.data.inputParams.systemVoltage,
          );

          if (selectedSolarTypeOption) {
            setSolarSystemType({
              value: selectedSolarTypeOption.value,
              label: selectedSolarTypeOption.label,
            });
          }

          if (selectedSystemVoltageOption) {
            setSystemVoltage({
              value: selectedSystemVoltageOption.value,
              label: selectedSystemVoltageOption.label,
            });
          }

          if (selectedInverterPhaseTypeOption) {
            setInverterPhaseType({
              value: selectedInverterPhaseTypeOption.value,
              label: selectedInverterPhaseTypeOption.label,
            });
          }

          if (selectedApplicationType) {
            setApplicationType({
              value: selectedApplicationType.value,
              label: selectedApplicationType.label,
            });
          }

          if (selectedUtilityOption) {
            setUtilityOption({
              value: selectedUtilityOption.value,
              label: selectedUtilityOption.label,
            });
          }

          if (res.estimationView.data.preferences) {
            setConfigurations(
              res.estimationView.data.preferences as Configurations,
            );
          }
        });

      return;
    }

    if (locationState) {
      triggerInitiateSolarEstimation({
        estimationType: locationState.solarSolutionType,
        applicationType:
          locationState.solarSolutionType === ESolarSolutionType.BOTH
            ? locationState.applicationType
            : null,
        utilityOptionType:
          locationState.solarSolutionType === ESolarSolutionType.BOTH
            ? locationState.utilityOption
            : null,
        solarEstimationType: EEstimationType.SIMPLIFIED,
        systemType: locationState.systemType as ESolarSystemType,
        inverterPhaseType:
          locationState.inverterPhaseType as EInverterPhaseType,
        monthlyRequiredEnergy: null,
        maxRequiredPower: null,
        numberOfUsers:
          locationState.solarSolutionType === ESolarSolutionType.BOTH
            ? locationState.numberOfUsers
            : null,
        demandPerUser:
          locationState.solarSolutionType === ESolarSolutionType.BOTH
            ? locationState.demandPerUser
            : null,
        systemVoltage: null,
        autonomyDays: null,
        nightLoad: null,
        sunlightHours: null,
        countryCode: country,
      })
        .unwrap()
        .then((res) => {
          const selectedSolarTypeOption = solarSystemTypesOption.find(
            (s) => s.value === locationState?.systemType,
          );

          const selectedInverterPhaseTypeOption = inverterPhaseTypeOptions.find(
            (i) => i.value === locationState.inverterPhaseType,
          );

          const selectedUtilityOption = utilityOptions.find(
            (u) => u.value === locationState.utilityOption,
          );

          const selectedApplicationType = applicationTypeOptions.find(
            (a) => a.value === locationState.applicationType,
          );

          if (selectedSolarTypeOption) {
            setSolarSystemType({
              value: selectedSolarTypeOption.value,
              label: selectedSolarTypeOption.label,
            });
          }

          if (selectedInverterPhaseTypeOption) {
            setInverterPhaseType({
              value: selectedInverterPhaseTypeOption.value,
              label: selectedInverterPhaseTypeOption.label,
            });
          }

          if (selectedApplicationType) {
            setApplicationType({
              value: selectedApplicationType.value,
              label: selectedApplicationType.label,
            });
          }

          if (selectedUtilityOption) {
            setUtilityOption({
              value: selectedUtilityOption.value,
              label: selectedUtilityOption.label,
            });
          }

          setNumberOfUsers(locationState.numberOfUsers);
          setDemandPerUser(locationState.demandPerUser);
          setSolarSolutionType(locationState.solarSolutionType);

          setEstimatorDetails({
            estimationId: res.id,
            estimationToken: res.token,
          });

          localStorage.setItem(
            ESolarSimLocalStorage.ESTIMATOR_DETAILS_SIMPLIFIED,
            JSON.stringify({
              estimationId: res.id,
              estimationToken: res.token,
            }),
          );

          triggerLazyViewSolarEstimation(
            auth.token
              ? {
                  estimationId: res.id,
                }
              : {
                  estimatorToken: res.token,
                },
          )
            .unwrap()
            .then((viewRes) => {
              setEstimationId(viewRes.estimationView.estimationId);
              // setConfigurations(
              //   viewRes.estimationView.data.preferences as Configurations
              // );
              setDischargeRate(
                viewRes.estimationView.data.inputParams
                  .dischargeRatePercentage ?? 0,
              );
              navigate(location.pathname, { replace: true, state: undefined });
            })
            .catch(() => {
              setEstimationId(0);
            });
        });
    }
  }, [
    triggerInitiateSolarEstimation,
    triggerLazyViewSolarEstimation,
    locationState,
    isUpdateCurrentState,
    auth.token,
  ]);

  useEffect(() => {
    const pathnameSlices = pathname.split("/");

    if (pathnameSlices.includes("simplified")) {
      if (pathnameSlices.includes("usage-info")) {
        setSelectedStep(1);
        return;
      }

      if (pathnameSlices.includes("assemble-system")) {
        setSelectedStep(2);
        return;
      }

      if (pathnameSlices.includes("system-summary")) {
        setSelectedStep(3);
        return;
      }
    }
  }, [pathname]);

  const onStepClick = (stepNumber: number) => {
    if (selectedStep === stepNumber) return;

    if (
      !devices.some(
        (device) =>
          Number(device.amount) > 0 &&
          Number(device.wattage) > 0 &&
          Number(device.usage) > 0,
      )
    ) {
      showErrorMessage(
        "Please add amount, wattage and usage for at least one device",
      );
      return;
    }

    if (!title.trim()) {
      showErrorMessage("Please provide a title before continuing");
      return;
    }

    if (auth.token) {
      if (stepNumber === 1) {
        navigate(AppRoute.DESIGN_SYSTEM_SIMPLIFIED_USAGE_INFO);
        return;
      }

      if (stepNumber === 2) {
        navigate(AppRoute.DESIGN_SYSTEM_SIMPLIFIED_ASSEMBLE_SYSTEM);
        return;
      }

      if (stepNumber === 3) {
        navigate(AppRoute.DESIGN_SYSTEM_SIMPLIFIED_SYSTEM_SUMMARY);
        return;
      }
      return;
    }
    if (stepNumber === 1) {
      navigate(AppRoute.PUBLIC_DESIGN_SYSTEM_SIMPLIFIED_USAGE_INFO);
      return;
    }

    if (stepNumber === 2) {
      navigate(AppRoute.PUBLIC_DESIGN_SYSTEM_SIMPLIFIED_ASSEMBLE_SYSTEM);
      return;
    }

    if (stepNumber === 3) {
      navigate(AppRoute.PUBLIC_DESIGN_SYSTEM_SIMPLIFIED_SYSTEM_SUMMARY);
    }
  };

  const onSave = async (
    devices: ISimplifiedDevice[],
    title: string,
    estimationType: ESolarSolutionType,
    applicationType: EApplicationType,
    utilityOptionType: EUtilityOptionType,
    systemType: ESolarSystemType,
    inverterPhaseType: EInverterPhaseType,
    monthlyEnergyNeed: number,
    monthlyEnergyNeedFromUser: number,
    maxPower: number,
    maxPowerFromUser: number,
    numberOfUsers: number,
    nightLoad: number,
    sunlightHours: number,
    demandPerUser: number | null,
    systemVoltage: number | null,
    autonomyDays: number | null,
    stepNumber?: number,
  ) => {
    if (selectedStep === stepNumber) return;

    if (
      !devices.some(
        (device) =>
          Number(device.amount) > 0 &&
          Number(device.wattage) > 0 &&
          Number(device.usage) > 0,
      ) &&
      !isRemoveWaterHeaterSystem &&
      !isAddWaterHeaterSystem
    ) {
      showErrorMessage(
        "Please add amount, wattage and usage for at least one device",
      );
      return;
    }

    if (
      !title.trim() &&
      !isRemoveWaterHeaterSystem &&
      !isAddWaterHeaterSystem
    ) {
      showErrorMessage("Please provide a title before continuing");
      return;
    }

    const simplifiedSave: ISimplifiedSave = {
      devices,
      title,
      estimationType,
      applicationType,
      utilityOptionType,
      systemType,
      inverterPhaseType,
      monthlyEnergyNeed,
      monthlyEnergyNeedFromUser,
      maxPower,
      maxPowerFromUser,
      numberOfUsers,
      nightLoad,
      sunlightHours,
      demandPerUser,
      systemVoltage,
      autonomyDays,
      isCalledSave: false,
      includeOtherFees: includeOtherFees,
    };

    if (!auth.token && !isRemoveWaterHeaterSystem && !isAddWaterHeaterSystem) {
      if (pathname.includes("usage-info")) {
        localStorage.setItem(
          "redirect",
          AppRoute.DESIGN_SYSTEM_SIMPLIFIED_USAGE_INFO,
        );
      } else if (pathname.includes("assemble-system")) {
        localStorage.setItem(
          "redirect",
          AppRoute.DESIGN_SYSTEM_SIMPLIFIED_ASSEMBLE_SYSTEM,
        );
      } else if (pathname.includes("system-summary")) {
        localStorage.setItem(
          "redirect",
          AppRoute.DESIGN_SYSTEM_SIMPLIFIED_SYSTEM_SUMMARY,
        );
      }

      localStorage.setItem("simplifiedSave", JSON.stringify(simplifiedSave));
      setIsShowSaveDataModal(true);
      return;
    }

    try {
      simplifiedSave.isCalledSave = true;
      localStorage.setItem("simplifiedSave", JSON.stringify(simplifiedSave));
      if (!estimatorDetails.estimationId || !estimatorDetails.estimationToken) {
        return;
      }

      const filteredDevices = devices.filter(
        (device) =>
          Number(device.amount) > 0 &&
          Number(device.wattage) > 0 &&
          Number(device.usage) > 0,
      );

      if (!isRemoveWaterHeaterSystem && !isAddWaterHeaterSystem) {
        await triggerPersistEstimation({
          estimationId: auth.token ? estimatorDetails.estimationId : "",
          estimationToken: auth.token ? "" : estimatorDetails.estimationToken,
          title,
          usages: filteredDevices.map((device) => ({
            name: device.deviceName,
            type: device.deviceType,
            amount: Number(device.amount),
            wattage: Number(device.wattage),
            usageInHours: Number(device.usage),
            timePeriod: ETimePeriod.WEEKLY,
          })),
          isDraft: true,
          countryCode: country,
        })
          .unwrap()
          .then((res: PersistEstimationResponseDTO) => {
            setScheduleId(res.scheduleId);
          });
      }

      await triggerUpdateEstimation({
        estimationId: auth.token ? estimatorDetails.estimationId : "",
        estimatorToken: auth.token ? "" : estimatorDetails.estimationToken,
        title: title ? title : null,
        estimationType,
        applicationType:
          estimationType === ESolarSolutionType.BOTH ? applicationType : null,
        utilityOptionType:
          estimationType === ESolarSolutionType.BOTH ? utilityOptionType : null,
        solarEstimationType: EEstimationType.SIMPLIFIED,
        systemType,
        inverterPhaseType: inverterPhaseType,
        monthlyRequiredEnergy: monthlyEnergyNeed + monthlyEnergyNeedFromUser,
        maxRequiredPower: maxPower + maxPowerFromUser * 1000,
        numberOfUsers:
          estimationType === ESolarSolutionType.BOTH ? numberOfUsers : null,
        demandPerUser:
          estimationType === ESolarSolutionType.BOTH ? demandPerUser : null,
        systemVoltage: systemVoltage,
        autonomyDays: autonomyDays,
        nightLoad: nightLoad,
        sunlightHours: sunlightHours,
        estimationSpaceClusterId: null,
        estimationBasicScheduleId: scheduleId ? scheduleId : null,
        isDraft: false,
        countryCode: country,
        installationCostRequested: includeOtherFees,
      })
        .unwrap()
        .then(() => {
          if (isRemoveWaterHeaterSystem) {
            showSuccessMessage("Water Heater System Deleted Successfully");
          }

          if (isAddWaterHeaterSystem) {
            showSuccessMessage("Water Heater System Added Successfully");
          }

          if (!isRemoveWaterHeaterSystem && !isAddWaterHeaterSystem) {
            showSuccessMessage("Estimation Saved Successfully");
          }

          if (stepNumber) {
            onStepClick(stepNumber);
          }

          setIsUpdateCurrentState((ps) => !ps);
          setIsRemoveWaterHeaterSystem(false);
          setIsShowRemoveWaterHeaterSystemModal(false);
          setIsShowRemoveSolarSystemModal(false);
          setIsAddWaterHeaterSystem(false);
          setIsShowWaterHeaterSystemModal(false);
        });
    } catch {
      showErrorMessage("Failed to save");
    }
  };

  useEffect(() => {
    const totalUnits = devices.reduce((sum, item) => sum + item.units, 0);
    const totalMaxPower = devices.reduce((sum, item) => sum + item.maxPower, 0);

    setMonthlyEnergyNeed(totalUnits * 30);
    setMaxPower(totalMaxPower);
  }, [devices]);

  useEffect(() => {
    if (!estimatorDetails.estimationId || !estimatorDetails.estimationToken) {
      return;
    }

    if (hasHydrated.current) {
      return;
    }
    hasHydrated.current = true;

    const simplifiedSave = localStorage.getItem("simplifiedSave");

    if (simplifiedSave) {
      const parsedSimplifiedSave: ISimplifiedSave = JSON.parse(simplifiedSave);

      const selectedSolarTypeOption = solarSystemTypesOption.find(
        (s) => s.value === parsedSimplifiedSave.systemType,
      );

      const selectedSystemVoltage = systemVoltageOptions.find(
        (s) => Number(s.value) === parsedSimplifiedSave.systemVoltage,
      );

      const selectedInverterPhaseTypeOption = inverterPhaseTypeOptions.find(
        (i) => i.value === parsedSimplifiedSave.inverterPhaseType,
      );

      const selectedUtilityOption = utilityOptions.find(
        (u) => u.value === parsedSimplifiedSave.utilityOptionType,
      );

      const selectedApplicationType = applicationTypeOptions.find(
        (a) => a.value === parsedSimplifiedSave.applicationType,
      );

      if (!parsedSimplifiedSave.isCalledSave) {
        onSave(
          parsedSimplifiedSave.devices,
          parsedSimplifiedSave.title,
          parsedSimplifiedSave.estimationType,
          parsedSimplifiedSave.applicationType,
          parsedSimplifiedSave.utilityOptionType,
          parsedSimplifiedSave.systemType,
          parsedSimplifiedSave.inverterPhaseType,
          parsedSimplifiedSave.monthlyEnergyNeed,
          parsedSimplifiedSave.monthlyEnergyNeedFromUser,
          parsedSimplifiedSave.maxPower,
          parsedSimplifiedSave.maxPowerFromUser,
          parsedSimplifiedSave.numberOfUsers,
          parsedSimplifiedSave.nightLoad,
          parsedSimplifiedSave.sunlightHours,
          parsedSimplifiedSave.demandPerUser,
          parsedSimplifiedSave.systemVoltage,
          parsedSimplifiedSave.autonomyDays,
        );
      }

      setTitle(parsedSimplifiedSave.title);
      setMonthlyEnergyNeed(parsedSimplifiedSave.monthlyEnergyNeed);
      setMonthlyEnergyNeedFromUser(
        parsedSimplifiedSave.monthlyEnergyNeedFromUser,
      );
      setMaxPower(parsedSimplifiedSave.maxPower);
      setMaxPowerFromUser(parsedSimplifiedSave.maxPowerFromUser);
      setNightLoad(parsedSimplifiedSave.nightLoad);
      setSunlightHours(parsedSimplifiedSave.sunlightHours);
      setDevices(parsedSimplifiedSave.devices);
      setIncludeOtherFees(parsedSimplifiedSave.includeOtherFees);
      setNumberOfUsers(parsedSimplifiedSave.numberOfUsers);
      setDemandPerUser(parsedSimplifiedSave.demandPerUser);
      setAutonomyDays(parsedSimplifiedSave.autonomyDays);
      setSolarSolutionType(parsedSimplifiedSave.estimationType);

      if (selectedSolarTypeOption) {
        setSolarSystemType({
          value: selectedSolarTypeOption.value,
          label: selectedSolarTypeOption.label,
        });
      }

      if (selectedSystemVoltage) {
        setSystemVoltage({
          value: selectedSystemVoltage.value,
          label: selectedSystemVoltage.label,
        });
      }

      if (selectedInverterPhaseTypeOption) {
        setInverterPhaseType({
          value: selectedInverterPhaseTypeOption.value,
          label: selectedInverterPhaseTypeOption.label,
        });
      }

      if (selectedApplicationType) {
        setApplicationType({
          value: selectedApplicationType.value,
          label: selectedApplicationType.label,
        });
      }

      if (selectedUtilityOption) {
        setUtilityOption({
          value: selectedUtilityOption.value,
          label: selectedUtilityOption.label,
        });
      }
    }
  }, [estimatorDetails.estimationId, estimatorDetails.estimationToken]);

  useEffect(() => {
    if (!pathname.includes("assemble-system")) return;

    const { estimationId, estimationToken } = estimatorDetails;

    if (!estimationId || !estimationToken || !country) {
      const unparsedEstimatorDetails = localStorage.getItem(
        ESolarSimLocalStorage.ESTIMATOR_DETAILS_SIMPLIFIED,
      );

      if (unparsedEstimatorDetails) {
        const parsedEstimatorDetails: IEstimatorDetails = JSON.parse(
          unparsedEstimatorDetails,
        );
        setEstimatorDetails(parsedEstimatorDetails);
        return;
      }

      if (auth.token) {
        navigate(AppRoute.DESIGN_SYSTEM_SIMPLIFIED_USAGE_INFO);
        return;
      }
      navigate(AppRoute.PUBLIC_DESIGN_SYSTEM_SIMPLIFIED_USAGE_INFO);
    }

    const fetchPreferences = async () => {
      setAssembleSystemData(undefined);
      try {
        if (!scheduleId) {
          const filteredDevices = devices.filter(
            (device) =>
              Number(device.amount) > 0 &&
              Number(device.wattage) > 0 &&
              Number(device.usage) > 0,
          );

          await triggerPersistEstimation({
            estimationId: auth.token ? estimatorDetails.estimationId : "",
            estimationToken: auth.token ? "" : estimatorDetails.estimationToken,
            title,
            usages: filteredDevices.map((device) => ({
              name: device.deviceName,
              type: device.deviceType,
              amount: Number(device.amount),
              wattage: Number(device.wattage),
              usageInHours: Number(device.usage),
              timePeriod: ETimePeriod.WEEKLY,
            })),
            isDraft: true,
            countryCode: country,
          })
            .unwrap()
            .then((pr) => {
              setScheduleId(pr.scheduleId);
            });

          await triggerUpdateEstimation({
            estimationId: auth.token ? estimatorDetails.estimationId : "",
            estimatorToken: auth.token ? "" : estimatorDetails.estimationToken,
            title: title ? title : null,
            estimationType: solarSolutionType,
            applicationType:
              solarSolutionType === ESolarSolutionType.BOTH
                ? (applicationType.value as EApplicationType)
                : null,
            utilityOptionType:
              solarSolutionType === ESolarSolutionType.BOTH
                ? (utilityOption.value as EUtilityOptionType)
                : null,
            solarEstimationType: EEstimationType.SIMPLIFIED,
            systemType: solarSystemType.value as ESolarSystemType,
            inverterPhaseType: inverterPhaseType.value as EInverterPhaseType,
            monthlyRequiredEnergy:
              monthlyEnergyNeed + monthlyEnergyNeedFromUser,
            maxRequiredPower: maxPower + maxPowerFromUser * 1000,
            numberOfUsers:
              solarSolutionType === ESolarSolutionType.BOTH
                ? numberOfUsers
                : null,
            demandPerUser:
              solarSolutionType === ESolarSolutionType.BOTH
                ? demandPerUser
                : null,
            nightLoad: nightLoad,
            sunlightHours: sunlightHours,
            systemVoltage: systemVoltage ? Number(systemVoltage.value) : null,
            autonomyDays: autonomyDays,
            estimationSpaceClusterId: null,
            estimationBasicScheduleId: scheduleId ? scheduleId : null,
            isDraft: false,
            countryCode: country,
            installationCostRequested: includeOtherFees,
          }).unwrap();

          return;
        }

        let brandPriority: ESolarSimBrands[] = [];

        if (configurations) {
          if (Array.isArray(configurations.BRAND_PRIORITY_LIST)) {
            brandPriority = configurations.BRAND_PRIORITY_LIST.map(
              (bp) => bp.brand,
            );
          }
        }

        const brandPriorityPreference = configurations
          ? Boolean(configurations.BRAND_PRIORITY)
          : false;
        const optimizeForCost = configurations
          ? Boolean(configurations.OPTIMIZE_FOR_COST)
          : false;
        const preferHighEfficiencyPanels = configurations
          ? Boolean(configurations.PREFER_HIGH_EFFICIENCY_PANELS)
          : false;
        const recommendExpandableSystems = configurations
          ? Boolean(configurations.RECOMMEND_EXPANDABLE_SYSTEMS)
          : false;

        if (brandPriority.length === 0) {
          return;
        }

        await triggerCreateSolarEstimation({
          estimationId: auth.token ? estimationId : "",
          estimatorToken: auth.token ? "" : estimationToken,
          title: title ? title : null,
          estimationType: solarSolutionType,
          applicationType:
            solarSolutionType === ESolarSolutionType.BOTH
              ? (applicationType.value as EApplicationType)
              : null,
          utilityOptionType:
            solarSolutionType === ESolarSolutionType.BOTH
              ? (utilityOption.value as EUtilityOptionType)
              : null,
          solarEstimationType: EEstimationType.SIMPLIFIED,
          systemType: solarSystemType.value as ESolarSystemType,
          inverterPhaseType: inverterPhaseType.value as EInverterPhaseType,
          monthlyRequiredEnergy: monthlyEnergyNeed + monthlyEnergyNeedFromUser,
          maxRequiredPower: maxPower + maxPowerFromUser * 1000,
          numberOfUsers:
            solarSolutionType === ESolarSolutionType.BOTH
              ? numberOfUsers
              : null,
          demandPerUser:
            solarSolutionType === ESolarSolutionType.BOTH
              ? demandPerUser
              : null,
          nightLoad: nightLoad,
          sunlightHours: sunlightHours,
          systemVoltage: systemVoltage ? Number(systemVoltage.value) : null,
          autonomyDays: autonomyDays,
          estimationSpaceClusterId: null,
          estimationBasicScheduleId: scheduleId,
          isDraft: false,
          brandPriority,
          brandPriorityPreference,
          optimizeForCost,
          preferHighEfficiencyPanels,
          recommendExpandableSystems,
          countryCode: country,
          hasChanged: hasChanged || null,
          controllerRequired: controllerRequired,
        })
          .unwrap()
          .then(() => {
            localStorage.removeItem("globalConfigurations");
          });

        const viewRes = await triggerLazyViewSolarEstimation(
          auth.token
            ? {
                estimationId: estimatorDetails.estimationId,
              }
            : {
                estimatorToken: estimatorDetails.estimationToken,
              },
        ).unwrap();

        setAssembleSystemData({ ...viewRes.estimationView.data });
        setDischargeRate(
          viewRes.estimationView.data.inputParams.dischargeRatePercentage ?? 0,
        );
        setSelectedSystemOutput(
          viewRes.estimationView.data.outputParams || {
            totalWattHours: 0,
            totalCost: 0,
            systemVoltage: 0,
            totalAmpHours: 0,
            nightLoadAmpHours: 0,
            batteryAmpHours: 0,
            maxPowerInWatt: 0,
            grandTotal: 0,
            totalAccessoriesCost: 0,
            laborCost: 0,
            totalLiters: 0,
          },
        );
      } catch {
        setAssembleSystemData(undefined);
      }
    };

    fetchPreferences();
  }, [
    pathname,
    estimatorDetails.estimationId,
    estimatorDetails.estimationToken,
    title,
    monthlyEnergyNeed,
    monthlyEnergyNeedFromUser,
    maxPower,
    maxPowerFromUser,
    nightLoad,
    sunlightHours,
    isResetRecommendations,
    country,
    scheduleId,
    devices,
    configurations,
    solarSystemType,
    inverterPhaseType,
    solarSolutionType,
    applicationType,
    utilityOption,
    numberOfUsers,
    demandPerUser,
    systemVoltage,
    autonomyDays,
    controllerRequired,
  ]);

  useEffect(() => {
    if (pathname.includes("system-summary")) {
      triggerLazyViewSolarEstimation(
        auth.token
          ? {
              estimationId: estimatorDetails.estimationId,
            }
          : {
              estimatorToken: estimatorDetails.estimationToken,
            },
      )
        .unwrap()
        .then((res) => {
          setComment(res.estimationView.adminComment || "");
          setSelectedSystemOutput(
            res.estimationView.data.outputParams || {
              totalWattHours: 0,
              totalCost: 0,
              systemVoltage: 0,
              totalAmpHours: 0,
              nightLoadAmpHours: 0,
              batteryAmpHours: 0,
              maxPowerInWatt: 0,
              grandTotal: 0,
              totalAccessoriesCost: 0,
              laborCost: 0,
              totalLiters: 0,
            },
          );
          setUserSelectedProducts(
            res.estimationView.data.userSelectedProducts || [],
          );
          setAssembleSystemData({ ...res.estimationView.data });
          setCurrency(res.estimationView.currency);
        })
        .catch(() => {
          setComment("");
          setSelectedSystemOutput({
            totalWattHours: 0,
            totalCost: 0,
            systemVoltage: 0,
            totalAmpHours: 0,
            nightLoadAmpHours: 0,
            batteryAmpHours: 0,
            maxPowerInWatt: 0,
            grandTotal: 0,
            totalAccessoriesCost: 0,
            laborCost: 0,
            totalLiters: 0,
          });
          setUserSelectedProducts([]);
          setAssembleSystemData(undefined);
          setCurrency("");
        });
    }
  }, [pathname, estimatorDetails.estimationToken]);

  useEffect(() => {
    if (!pathname.includes("system-summary")) {
      setUserSelectedProducts([]);
      setAssembleSystemData(undefined);
      setCurrency("");
    }

    if (!pathname.includes("assemble-system")) {
      setAssembleSystemData(undefined);
    }
  }, [pathname]);

  const onEditParameter = (type: EEnergyNeedEditParameter, value: string) => {
    let newValue: string | number;
    if (type === EEnergyNeedEditParameter.TITLE) {
      newValue = value;
    } else if (value.trim() === "") {
      newValue = 0;
    } else {
      newValue = value;
    }

    switch (type) {
      case EEnergyNeedEditParameter.TITLE:
        setTitle(newValue as string);
        break;
      case EEnergyNeedEditParameter.MONTHLY_ENERGY_NEED: {
        const previousValue = monthlyEnergyNeedFromUser;
        const nowValue = Number.isNaN(Number(newValue)) ? 0 : Number(newValue);

        if (previousValue !== nowValue) {
          setMonthlyEnergyNeedFromUser(
            Number.isNaN(Number(newValue)) ? 0 : Number(newValue),
          );
          setIsReset(true);
        }
        break;
      }
      case EEnergyNeedEditParameter.MAX_POWER: {
        const previousValue = maxPowerFromUser;
        const nowValue = Number.isNaN(Number(newValue)) ? 0 : Number(newValue);

        if (previousValue !== nowValue) {
          setMaxPowerFromUser(
            Number.isNaN(Number(newValue)) ? 0 : Number(newValue),
          );
          setIsReset(true);
        }
        break;
      }
      case EEnergyNeedEditParameter.NIGHT_LOAD: {
        const previousValue = nightLoad;
        const nowValue = Number.isNaN(Number(newValue)) ? 0 : Number(newValue);

        if (previousValue !== nowValue) {
          setNightLoad(Number.isNaN(Number(newValue)) ? 0 : Number(newValue));
          setIsReset(true);
        }
        break;
      }
      case EEnergyNeedEditParameter.SUNLIGHT_HOURS: {
        const previousValue = sunlightHours;
        const nowValue = Number.isNaN(Number(newValue)) ? 0 : Number(newValue);

        if (previousValue !== nowValue) {
          setSunlightHours(
            Number.isNaN(Number(newValue)) ? 0 : Number(newValue),
          );
          setIsReset(true);
        }
        break;
      }
      case EEnergyNeedEditParameter.NO_OF_USERS: {
        const previousValue = numberOfUsers;
        const nowValue = Number.isNaN(Number(newValue)) ? 0 : Number(newValue);

        if (previousValue !== nowValue) {
          setNumberOfUsers(
            Number.isNaN(Number(newValue)) ? 0 : Number(newValue),
          );
          setIsReset(true);
        }

        break;
      }
      case EEnergyNeedEditParameter.DEMAND_PER_USER: {
        const previousValue = demandPerUser;
        const nowValue = Number.isNaN(Number(newValue)) ? 0 : Number(newValue);

        if (previousValue !== nowValue) {
          setDemandPerUser(
            Number.isNaN(Number(newValue)) ? 0 : Number(newValue),
          );
          setIsReset(true);
        }
        break;
      }
      case EEnergyNeedEditParameter.AUTONOMY_DAYS: {
        const previousValue = autonomyDays;
        const nowValue = Number.isNaN(Number(newValue)) ? 0 : Number(newValue);

        if (previousValue !== nowValue) {
          setAutonomyDays(
            Number.isNaN(Number(newValue)) ? 0 : Number(newValue),
          );
          setIsReset(true);
        }
        break;
      }
      default:
        break;
    }

    setShowEditParameterModal(false);
  };

  const onParameterEdit = (type: EEnergyNeedEditParameter) => {
    setEditParameterType(type);

    let parameterValue: number | string;
    switch (type) {
      case EEnergyNeedEditParameter.TITLE:
        parameterValue = title;
        break;
      case EEnergyNeedEditParameter.MONTHLY_ENERGY_NEED:
        parameterValue = monthlyEnergyNeedFromUser;
        break;
      case EEnergyNeedEditParameter.MAX_POWER:
        parameterValue = maxPowerFromUser;
        break;
      case EEnergyNeedEditParameter.NIGHT_LOAD:
        parameterValue = nightLoad;
        break;
      case EEnergyNeedEditParameter.SUNLIGHT_HOURS:
        parameterValue = sunlightHours;
        break;
      case EEnergyNeedEditParameter.DEMAND_PER_USER:
        parameterValue = demandPerUser ?? 0;
        break;
      case EEnergyNeedEditParameter.AUTONOMY_DAYS:
        parameterValue = autonomyDays ?? 0;
        break;
      default:
        parameterValue = numberOfUsers;
        break;
    }
    setEditParameterValue(parameterValue);

    let estimatedVal: number | undefined;
    if (type === EEnergyNeedEditParameter.MONTHLY_ENERGY_NEED) {
      estimatedVal = monthlyEnergyNeed;
    } else if (type === EEnergyNeedEditParameter.MAX_POWER) {
      estimatedVal = maxPower / 1000;
    } else {
      estimatedVal = undefined;
    }
    setEstimatedValue(estimatedVal);

    setShowEditParameterModal(true);
  };

  return (
    <>
      <StepsHeader
        steps={steps}
        selectedStep={selectedStep}
        onStepClick={(stepNumber: number) => {
          if (!auth.token) {
            if (stepNumber < selectedStep && stepNumber === 1) {
              navigate(AppRoute.PUBLIC_DESIGN_SYSTEM_SIMPLIFIED_USAGE_INFO);
              return;
            }

            if (stepNumber < selectedStep && stepNumber === 2) {
              navigate(
                AppRoute.PUBLIC_DESIGN_SYSTEM_SIMPLIFIED_ASSEMBLE_SYSTEM,
              );
              return;
            }

            if (stepNumber < selectedStep && stepNumber === 3) {
              navigate(AppRoute.PUBLIC_DESIGN_SYSTEM_SIMPLIFIED_SYSTEM_SUMMARY);
              return;
            }

            onStepClick(stepNumber);
            return;
          }

          if (stepNumber < selectedStep && stepNumber === 1) {
            navigate(AppRoute.DESIGN_SYSTEM_SIMPLIFIED_USAGE_INFO);
            return;
          }

          if (stepNumber < selectedStep && stepNumber === 2) {
            navigate(AppRoute.DESIGN_SYSTEM_SIMPLIFIED_ASSEMBLE_SYSTEM);
            return;
          }

          if (stepNumber < selectedStep && stepNumber === 3) {
            navigate(AppRoute.DESIGN_SYSTEM_SIMPLIFIED_SYSTEM_SUMMARY);
            return;
          }

          onSave(
            devices,
            title,
            solarSolutionType,
            applicationType.value as EApplicationType,
            utilityOption.value as EUtilityOptionType,
            solarSystemType.value as ESolarSystemType,
            inverterPhaseType.value as EInverterPhaseType,
            monthlyEnergyNeed,
            monthlyEnergyNeedFromUser,
            maxPower,
            maxPowerFromUser,
            numberOfUsers,
            nightLoad,
            sunlightHours,
            demandPerUser,
            systemVoltage ? Number(systemVoltage.value) : null,
            autonomyDays,
            stepNumber,
          );
        }}
        onSave={() => {
          onSave(
            devices,
            title,
            solarSolutionType,
            applicationType.value as EApplicationType,
            utilityOption.value as EUtilityOptionType,
            solarSystemType.value as ESolarSystemType,
            inverterPhaseType.value as EInverterPhaseType,
            monthlyEnergyNeed,
            monthlyEnergyNeedFromUser,
            maxPower,
            maxPowerFromUser,
            numberOfUsers,
            nightLoad,
            sunlightHours,
            demandPerUser,
            systemVoltage ? Number(systemVoltage.value) : null,
            autonomyDays,
          );
        }}
        isApproved={false}
        onChangeMode={() => {
          const advancedSave: IAdvancedSave = {
            estimationType: solarSolutionType,
            applicationType: applicationType.value as EApplicationType,
            utilityOption: utilityOption.value as EUtilityOptionType,
            systemType: solarSystemType.value as ESolarSystemType,
            numberOfUsers,
            demandPerUser,
            inverterPhaseType: inverterPhaseType.value as EInverterPhaseType,
            estimationId: estimatorDetails.estimationId,
          };

          if (!auth.token) {
            localStorage.setItem(
              "redirect",
              AppRoute.DESIGN_SYSTEM_ADVANCED_DEVICES,
            );
            localStorage.setItem("advancedSave", JSON.stringify(advancedSave));
            navigate(AppRoute.LOGIN);
            return;
          }

          navigate(AppRoute.DESIGN_SYSTEM_ADVANCED_DEVICES, {
            state: {
              systemType: solarSystemType.value as ESolarSystemType,
              numberOfUsers,
              demandPerUser,
              applicationType: applicationType.value as EApplicationType,
              utilityOption: utilityOption.value as EUtilityOptionType,
              solarSolutionType,
              inverterPhaseType: inverterPhaseType.value as EInverterPhaseType,
            },
          });
        }}
      />
      <div className={`py-3 ${styles.mainContent}`}>
        <div
          className={`estimation-container rounded-3 ${
            auth.token ? "" : "mx-4"
          }`}
        >
          <Row
            className={`align-items-center justify-content-between py-2 px-4 ${
              solarSolutionType === ESolarSolutionType.BOTH ? `mb-2` : ``
            }`}
          >
            <Col className="col-auto">
              <Row>
                <Col className="text-color-3 font-size-14">#{estimationId}</Col>
              </Row>
              <Row className="align-items-center">
                <Col className="col-auto title-2 font-weight-600 font-size-18 pe-0">
                  {title === "" ? "Title Goes Here" : title}
                </Col>
                <Col className="col-auto ps-1">
                  <MaterialIcon
                    icon="border_color"
                    color="var(--color-edit-icon-2)"
                    size={12}
                    className="cursor-pointer"
                    onClick={() => {
                      onParameterEdit(EEnergyNeedEditParameter.TITLE);
                    }}
                  />
                </Col>
              </Row>
            </Col>
            <Col
              className={
                solarSolutionType === ESolarSolutionType.BOTH
                  ? `col-12 mt-2`
                  : `col-12 col-xxl-auto mt-3 mt-xxl-0`
              }
            >
              <Row className="align-items-stretch gx-2">
                <Col
                  className={
                    solarSolutionType === ESolarSolutionType.BOTH
                      ? `col-12 col-xxl-7`
                      : ``
                  }
                >
                  <SelectedSolarSystemCard
                    monthlyEnergyNeed={monthlyEnergyNeed}
                    maxPower={maxPower}
                    dischargeRate={dischargeRate}
                    nightLoad={nightLoad}
                    sunlightHours={sunlightHours}
                    monthlyEnergyNeedFromUser={monthlyEnergyNeedFromUser}
                    maxPowerFromUser={maxPowerFromUser}
                    onParameterEdit={onParameterEdit}
                    totalWattHours={selectedSystemOutput.totalWattHours}
                    maxPowerInWatt={selectedSystemOutput.maxPowerInWatt}
                    solarSystemType={solarSystemType}
                    onChangeSolarSystemType={(option: Option) => {
                      setSolarSystemType(option);
                      setAutonomyDays(null);
                      setIsReset(true);
                    }}
                    inverterPhaseType={inverterPhaseType}
                    onChangeInverterPhaseType={(option: Option) => {
                      setInverterPhaseType(option);
                      setIsReset(true);
                    }}
                    isHideDeleteIcon={
                      solarSolutionType !== ESolarSolutionType.BOTH ||
                      selectedStep === 3
                    }
                    onDeleteSolarSystem={() =>
                      setIsShowRemoveSolarSystemModal(true)
                    }
                    systemVoltage={systemVoltage}
                    onSystemVoltageChange={(option: Option) => {
                      setSystemVoltage(option);
                      setIsReset(true);
                    }}
                    autonomyDays={autonomyDays}
                    isHideEditIcon={selectedStep === 3}
                  />
                </Col>
                <Col
                  className={
                    solarSolutionType === ESolarSolutionType.BOTH
                      ? `col-12 col-xxl-5 mt-3 mt-xxl-0`
                      : `col-12 col-xxl-auto mt-3 mt-xxl-0`
                  }
                >
                  {solarSolutionType === ESolarSolutionType.BOTH ? (
                    <SelectedWaterHeaterSystemCard
                      onParameterEdit={onParameterEdit}
                      waterDemand={waterDemand}
                      totalLiters={selectedSystemOutput.totalLiters || 0}
                      numberOfUsers={numberOfUsers}
                      demandPerUser={demandPerUser}
                      applicationType={applicationType}
                      utilityOption={utilityOption}
                      onApplicationTypeChange={(option: Option) => {
                        setApplicationType(option);
                        setIsReset(true);
                      }}
                      onUtilityOptionChange={(option: Option) => {
                        setUtilityOption(option);
                        setIsReset(true);
                      }}
                      onDeleteWaterHeaterSystem={() => {
                        setIsShowRemoveWaterHeaterSystemModal(true);
                        setIsRemoveWaterHeaterSystem(true);
                      }}
                      isHideDeleteIcon={selectedStep === 3}
                      isHideEditIcon={selectedStep === 3}
                    />
                  ) : (
                    <>
                      {selectedStep === 3 ? (
                        <></>
                      ) : (
                        <IncludeSystemCard
                          icon="water_drop"
                          text="Include Solar Water Heating System as Well?"
                          onClick={() => {
                            setIsShowWaterHeaterSystemModal(true);
                            setIsAddWaterHeaterSystem(true);
                          }}
                        />
                      )}
                    </>
                  )}
                </Col>
              </Row>
            </Col>
          </Row>
          {selectedStep === 1 && (
            <AddUsageInfo devices={devices} setDevices={setDevices} />
          )}
          {selectedStep === 2 && (
            <AssembleSystem
              assembleSystemData={assembleSystemData}
              estimatorDetails={estimatorDetails}
              selectedSystemOutput={selectedSystemOutput}
              setSelectedSystemOutput={setSelectedSystemOutput}
              setIsResetRecommendations={setIsResetRecommendations}
              configurations={configurations}
              sendConfigurations={(configurations: Configurations) => {
                setConfigurations({ ...configurations });
              }}
              isReset={isReset}
              setIsReset={setIsReset}
              setHasChanged={setHasChanged}
              estimationNo={estimationId}
              setDischargeRate={setDischargeRate}
              estimationType={solarSolutionType}
              setWaterDemand={setWaterDemand}
              controllerRequired={controllerRequired}
              setControllerRequired={setControllerRequired}
              systemType={solarSystemType.value as ESolarSystemType}
              isDisabledControllerSwitch={
                isLoadingCreateSolarEstimation ||
                isLoadingViewEstimation ||
                isLoadingUpdateEstimation
              }
            />
          )}
          {selectedStep === 3 && (
            <ApprovedSystem
              userSelectedProducts={userSelectedProducts}
              assembleSystemData={assembleSystemData}
              onSave={() => {
                onSave(
                  devices,
                  title,
                  solarSolutionType,
                  applicationType.value as EApplicationType,
                  utilityOption.value as EUtilityOptionType,
                  solarSystemType.value as ESolarSystemType,
                  inverterPhaseType.value as EInverterPhaseType,
                  monthlyEnergyNeed,
                  monthlyEnergyNeedFromUser,
                  maxPower,
                  maxPowerFromUser,
                  numberOfUsers,
                  nightLoad,
                  sunlightHours,
                  demandPerUser,
                  systemVoltage ? Number(systemVoltage.value) : null,
                  autonomyDays,
                );
              }}
              currency={currency}
              estimatorDetails={estimatorDetails}
              includeOtherFees={includeOtherFees}
              setIncludeOtherFees={setIncludeOtherFees}
              comment={comment}
              devices={devices}
              title={title}
              country={country}
              monthlyRequiredEnergy={
                monthlyEnergyNeed + monthlyEnergyNeedFromUser
              }
              maxRequiredPower={maxPower + maxPowerFromUser * 1000}
              systemType={solarSystemType.value as ESolarSystemType}
              inverterPhaseType={inverterPhaseType.value as EInverterPhaseType}
              nightLoad={nightLoad}
              sunlightHours={sunlightHours}
              estimationSpaceClusterId={null}
              estimationNo={estimationId}
              estimationType={solarSolutionType}
              applicationType={applicationType.value as EApplicationType}
              utilityOptionType={utilityOption.value as EUtilityOptionType}
              numberOfUsers={numberOfUsers}
              demandPerUser={demandPerUser}
              systemVoltage={systemVoltage ? Number(systemVoltage.value) : null}
              autonomyDays={autonomyDays}
            />
          )}
        </div>
      </div>
      <EditParameterModal
        show={showEditParameterModal}
        onCancel={() => {
          setShowEditParameterModal(false);
        }}
        onClose={() => {
          setShowEditParameterModal(false);
        }}
        type={editParameterType}
        currentValue={editParameterValue}
        onEditParameter={onEditParameter}
        estimated={estimatedValue}
      />
      <SpinnerModal
        show={
          isLoadingInitiateSolarEstimation ||
          isLoadingPersistEstimation ||
          isLoadingUpdateEstimation ||
          isLoadingViewEstimation ||
          isLoadingCreateSolarEstimation
        }
      />
      <SaveDataModal
        show={isShowSaveDataModal}
        onClose={() => {
          const simplifiedSave = localStorage.getItem("simplifiedSave");
          if (simplifiedSave) {
            const parsedSimplifiedSave: ISimplifiedSave =
              JSON.parse(simplifiedSave);
            parsedSimplifiedSave.isCalledSave = true;
            localStorage.setItem(
              "simplifiedSave",
              JSON.stringify(parsedSimplifiedSave),
            );
          }

          localStorage.removeItem("redirect");

          setIsShowSaveDataModal(false);
        }}
        onConfirm={() => {
          navigate(AppRoute.LOGIN);
          setIsShowSaveDataModal(false);
        }}
      />
      <RemoveSolarSystemModal
        show={isShowRemoveSolarSystemModal}
        onClose={() => setIsShowRemoveSolarSystemModal(false)}
        onCancel={() => setIsShowRemoveSolarSystemModal(false)}
        onConfirm={() => {
          triggerUpdateEstimation({
            estimationId: auth.token ? estimatorDetails.estimationId : "",
            estimatorToken: auth.token ? "" : estimatorDetails.estimationToken,
            title: title ? title : null,
            estimationType: ESolarSolutionType.WATER_HEATING_SYSTEM,
            applicationType: applicationType.value as EApplicationType,
            utilityOptionType: utilityOption.value as EUtilityOptionType,
            solarEstimationType: null,
            systemType: null,
            inverterPhaseType: null,
            monthlyRequiredEnergy: null,
            maxRequiredPower: null,
            numberOfUsers: numberOfUsers,
            demandPerUser: demandPerUser,
            nightLoad: null,
            sunlightHours: null,
            systemVoltage: null,
            autonomyDays: null,
            estimationSpaceClusterId: null,
            estimationBasicScheduleId: null,
            isDraft: false,
            countryCode: country,
            installationCostRequested: includeOtherFees,
          })
            .unwrap()
            .then(() => {
              showSuccessMessage("Solar Energy System Deleted Successfully");
              setIsShowRemoveSolarSystemModal(false);

              if (auth.token) {
                navigate(AppRoute.DESIGN_SYSTEM_WATER_HEATER_ASSEMBLE_SYSTEM, {
                  state: {
                    estimationId: estimatorDetails.estimationId,
                    estimationToken: estimatorDetails.estimationToken,
                  },
                });
                return;
              }

              navigate(
                AppRoute.PUBLIC_DESIGN_SYSTEM_WATER_HEATER_ASSEMBLE_SYSTEM,
                {
                  state: {
                    estimationId: estimatorDetails.estimationId,
                    estimationToken: estimatorDetails.estimationToken,
                  },
                },
              );
            });
        }}
        isLoading={isLoadingUpdateEstimation}
      />
      <RemoveWaterHeaterSystemModal
        show={isShowRemoveWaterHeaterSystemModal}
        onClose={() => {
          setIsShowRemoveWaterHeaterSystemModal(false);
          setIsRemoveWaterHeaterSystem(false);
        }}
        onCancel={() => {
          setIsShowRemoveWaterHeaterSystemModal(false);
          setIsRemoveWaterHeaterSystem(false);
        }}
        onConfirm={() => {
          onSave(
            devices,
            title,
            ESolarSolutionType.SOLAR_ENERGY_SYSTEM,
            applicationType.value as EApplicationType,
            utilityOption.value as EUtilityOptionType,
            solarSystemType.value as ESolarSystemType,
            inverterPhaseType.value as EInverterPhaseType,
            monthlyEnergyNeed,
            monthlyEnergyNeedFromUser,
            maxPower,
            maxPowerFromUser,
            numberOfUsers,
            nightLoad,
            sunlightHours,
            demandPerUser,
            systemVoltage ? Number(systemVoltage.value) : null,
            autonomyDays,
          );
        }}
        isLoading={isLoadingUpdateEstimation}
      />
      <WaterHeaterSystemModal
        show={isShowWaterHeaterSystemModal}
        onClose={() => {
          setIsShowWaterHeaterSystemModal(false);
          setIsAddWaterHeaterSystem(false);
        }}
        onCancel={() => {
          setIsShowWaterHeaterSystemModal(false);
          setIsAddWaterHeaterSystem(false);
        }}
        onSave={(
          applicationType,
          utilityOption,
          numberOfUsers,
          demandPerUser,
        ) => {
          onSave(
            devices,
            title,
            ESolarSolutionType.BOTH,
            applicationType,
            utilityOption,
            solarSystemType.value as ESolarSystemType,
            inverterPhaseType.value as EInverterPhaseType,
            monthlyEnergyNeed,
            monthlyEnergyNeedFromUser,
            maxPower,
            maxPowerFromUser,
            numberOfUsers,
            nightLoad,
            sunlightHours,
            demandPerUser,
            systemVoltage ? Number(systemVoltage.value) : null,
            autonomyDays,
          );
        }}
        isLoading={isLoadingUpdateEstimation}
      />
    </>
  );
};

export default BuildSystemSimplified;
